import { CircleOutline } from './CircleOutline';
import { RoundedDiamond } from './RoundedDiamond';
import { DottedArc } from './DottedArc';
import { AccentMark } from './AccentMark';
import { DotGrid } from './DotGrid';
import { WaveLine } from './WaveLine';
import { GlowOrb } from './GlowOrb';
import { CrosshatchPattern } from './CrosshatchPattern';

type DecorationPreset = 'hero' | 'features' | 'reviews' | 'cta' | 'pricing' | 'signup' | 'minimal' | 'landing-hero' | 'landing-content' | 'seo-hero' | 'seo-content' | 'seo-features' | 'seo-reviews' | 'seo-faq' | 'seo-cta';

interface PageDecorationsProps {
  preset?: DecorationPreset;
  className?: string;
  opacityMultiplier?: number;
}

export function PageDecorations({ preset = 'hero', className = '', opacityMultiplier = 1 }: PageDecorationsProps) {
  const getOpacity = (base: number) => Math.min(1, Math.max(0, base * opacityMultiplier));

  const renderDecorations = () => {
    switch (preset) {
      case 'hero':
        return (
          <>
            {/* Top left concentric circles */}
            <div className="absolute top-8 left-12 hidden md:block">
              <CircleOutline size={95} color="primary" opacity={getOpacity(0.48)} concentric animate />
            </div>
            {/* Left side dashed arc */}
            <div className="absolute top-36 left-6 hidden lg:block">
              <DottedArc width={160} height={110} color="primary" opacity={getOpacity(0.42)} direction="left" />
            </div>
            {/* Top right stacked diamonds */}
            <div className="absolute top-14 right-16 hidden md:block">
              <RoundedDiamond size={55} color="primary" opacity={getOpacity(0.48)} rotation={45} stacked />
            </div>
            {/* Right side filled diamond */}
            <div className="absolute top-52 right-10 hidden lg:block">
              <RoundedDiamond size={42} color="secondary" filled opacity={getOpacity(0.38)} rotation={30} />
            </div>
            {/* Right side small circle */}
            <div className="absolute top-36 right-28 hidden lg:block">
              <CircleOutline size={48} color="secondary" opacity={getOpacity(0.42)} />
            </div>
            {/* Bottom right dashed arc */}
            <div className="absolute bottom-20 right-4 hidden lg:block">
              <DottedArc width={130} height={90} color="secondary" opacity={getOpacity(0.38)} direction="right" variant="dotted" />
            </div>
            {/* Scattered accent marks */}
            <div className="absolute bottom-20 right-20 hidden md:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.55)} />
            </div>
            <div className="absolute bottom-40 right-44 hidden lg:block">
              <AccentMark size={18} type="plus" color="primary" opacity={getOpacity(0.50)} />
            </div>
            <div className="absolute top-60 right-32 hidden lg:block">
              <AccentMark size={14} type="dot" color="primary" opacity={getOpacity(0.48)} />
            </div>
            <div className="absolute top-80 right-12 hidden lg:block">
              <AccentMark size={24} type="hash" color="secondary" opacity={getOpacity(0.40)} />
            </div>
            {/* Bottom left circle */}
            <div className="absolute bottom-44 left-14 hidden lg:block">
              <CircleOutline size={65} color="secondary" opacity={getOpacity(0.40)} concentric />
            </div>
            {/* Left side accent marks */}
            <div className="absolute top-80 left-24 hidden lg:block">
              <AccentMark size={20} type="plus" color="secondary" opacity={getOpacity(0.48)} />
            </div>
            <div className="absolute bottom-28 left-8 hidden lg:block">
              <AccentMark size={16} type="x" color="primary" opacity={getOpacity(0.45)} />
            </div>
            {/* Mid left diamond */}
            <div className="absolute top-64 left-6 hidden lg:block">
              <RoundedDiamond size={38} color="primary" opacity={getOpacity(0.36)} rotation={50} />
            </div>
            {/* Bottom right circle */}
            <div className="absolute bottom-12 right-32 hidden lg:block">
              <CircleOutline size={55} color="primary" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'features':
        return (
          <>
            {/* Top right concentric circle */}
            <div className="absolute top-18 right-20 hidden md:block">
              <CircleOutline size={88} color="primary" opacity={getOpacity(0.45)} concentric animate />
            </div>
            {/* Top right dashed arc */}
            <div className="absolute top-8 right-8 hidden lg:block">
              <DottedArc width={140} height={95} color="primary" opacity={getOpacity(0.40)} direction="right" />
            </div>
            {/* Left stacked diamonds */}
            <div className="absolute top-52 left-12 hidden lg:block">
              <RoundedDiamond size={52} color="secondary" opacity={getOpacity(0.45)} rotation={40} stacked />
            </div>
            {/* Left small diamond */}
            <div className="absolute top-28 left-6 hidden lg:block">
              <RoundedDiamond size={36} color="primary" opacity={getOpacity(0.38)} rotation={55} />
            </div>
            {/* Bottom right diamond */}
            <div className="absolute bottom-40 right-12 hidden md:block">
              <RoundedDiamond size={58} color="primary" opacity={getOpacity(0.42)} rotation={50} stacked animate />
            </div>
            {/* Left dashed arc */}
            <div className="absolute top-36 left-2 hidden lg:block">
              <DottedArc width={130} height={88} color="secondary" opacity={getOpacity(0.38)} direction="left" variant="dotted" />
            </div>
            {/* Scattered accent marks */}
            <div className="absolute bottom-52 left-18 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.52)} />
            </div>
            <div className="absolute top-68 left-32 hidden lg:block">
              <AccentMark size={18} type="plus" color="primary" opacity={getOpacity(0.48)} />
            </div>
            <div className="absolute bottom-28 right-32 hidden md:block">
              <CircleOutline size={55} color="secondary" opacity={getOpacity(0.42)} />
            </div>
            <div className="absolute top-32 left-24 hidden lg:block">
              <AccentMark size={14} type="dot" color="secondary" opacity={getOpacity(0.46)} />
            </div>
            <div className="absolute bottom-60 right-18 hidden lg:block">
              <AccentMark size={20} type="hash" color="primary" opacity={getOpacity(0.42)} />
            </div>
            {/* Right side circle */}
            <div className="absolute top-56 right-6 hidden lg:block">
              <CircleOutline size={52} color="primary" opacity={getOpacity(0.38)} />
            </div>
            {/* Bottom accent marks */}
            <div className="absolute bottom-16 left-36 hidden lg:block">
              <AccentMark size={16} type="plus" color="secondary" opacity={getOpacity(0.44)} />
            </div>
            {/* Bottom left circle */}
            <div className="absolute bottom-20 left-8 hidden lg:block">
              <CircleOutline size={60} color="primary" opacity={getOpacity(0.36)} concentric />
            </div>
          </>
        );

      case 'reviews':
        return (
          <>
            {/* Top left circle */}
            <div className="absolute top-14 left-14 hidden md:block">
              <CircleOutline size={80} color="primary" opacity={getOpacity(0.44)} concentric />
            </div>
            {/* Top left dashed arc */}
            <div className="absolute top-6 left-6 hidden lg:block">
              <DottedArc width={120} height={80} color="primary" opacity={getOpacity(0.38)} direction="left" />
            </div>
            {/* Bottom right concentric circle */}
            <div className="absolute bottom-20 right-20 hidden md:block">
              <CircleOutline size={100} color="primary" opacity={getOpacity(0.42)} concentric animate />
            </div>
            {/* Top right stacked diamonds */}
            <div className="absolute top-32 right-28 hidden lg:block">
              <RoundedDiamond size={48} color="primary" opacity={getOpacity(0.44)} rotation={45} stacked />
            </div>
            {/* Top right filled diamond */}
            <div className="absolute top-48 right-12 hidden lg:block">
              <RoundedDiamond size={38} color="secondary" filled opacity={getOpacity(0.38)} rotation={35} />
            </div>
            {/* Right dashed arc */}
            <div className="absolute top-16 right-4 hidden lg:block">
              <DottedArc width={110} height={75} color="secondary" opacity={getOpacity(0.36)} direction="right" variant="dotted" />
            </div>
            {/* Accent marks */}
            <div className="absolute bottom-40 left-24 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.50)} />
            </div>
            <div className="absolute top-60 left-8 hidden lg:block">
              <DottedArc width={100} height={70} color="primary" opacity={getOpacity(0.36)} direction="left" variant="dotted" />
            </div>
            <div className="absolute bottom-56 right-18 hidden lg:block">
              <AccentMark size={18} type="plus" color="primary" opacity={getOpacity(0.46)} />
            </div>
            <div className="absolute top-72 right-36 hidden lg:block">
              <AccentMark size={14} type="dot" color="secondary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute bottom-28 left-10 hidden lg:block">
              <RoundedDiamond size={32} color="primary" opacity={getOpacity(0.36)} rotation={40} />
            </div>
            <div className="absolute top-40 left-28 hidden lg:block">
              <AccentMark size={20} type="hash" color="primary" opacity={getOpacity(0.40)} />
            </div>
            {/* Bottom left circle */}
            <div className="absolute bottom-12 left-6 hidden lg:block">
              <CircleOutline size={50} color="secondary" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'cta':
        return (
          <>
            {/* Top left concentric circle */}
            <div className="absolute top-8 left-12 hidden md:block">
              <CircleOutline size={85} color="neutral" opacity={getOpacity(0.48)} concentric animate />
            </div>
            {/* Top left dashed arc */}
            <div className="absolute top-32 left-4 hidden lg:block">
              <DottedArc width={120} height={80} color="neutral" opacity={getOpacity(0.42)} direction="left" />
            </div>
            {/* Bottom right stacked diamond */}
            <div className="absolute bottom-14 right-14 hidden md:block">
              <RoundedDiamond size={54} color="neutral" opacity={getOpacity(0.45)} rotation={45} stacked />
            </div>
            {/* Top right accent */}
            <div className="absolute top-20 right-28 hidden lg:block">
              <AccentMark size={24} type="plus" color="neutral" opacity={getOpacity(0.55)} />
            </div>
            {/* Bottom left accent */}
            <div className="absolute bottom-24 left-24 hidden lg:block">
              <AccentMark size={26} type="x" color="neutral" opacity={getOpacity(0.50)} />
            </div>
            {/* Right dashed arc */}
            <div className="absolute top-12 right-4 hidden lg:block">
              <DottedArc width={110} height={75} color="neutral" opacity={getOpacity(0.40)} direction="right" variant="dotted" />
            </div>
            {/* Bottom right circle */}
            <div className="absolute bottom-8 right-4 hidden lg:block">
              <CircleOutline size={60} color="neutral" opacity={getOpacity(0.40)} />
            </div>
            {/* Dot accents */}
            <div className="absolute top-44 right-8 hidden lg:block">
              <AccentMark size={16} type="dot" color="neutral" opacity={getOpacity(0.48)} />
            </div>
            <div className="absolute bottom-44 left-8 hidden lg:block">
              <AccentMark size={14} type="dot" color="neutral" opacity={getOpacity(0.44)} />
            </div>
            {/* Small diamond */}
            <div className="absolute top-36 left-20 hidden lg:block">
              <RoundedDiamond size={36} color="neutral" opacity={getOpacity(0.38)} rotation={50} />
            </div>
            {/* Hash accent */}
            <div className="absolute bottom-36 right-32 hidden lg:block">
              <AccentMark size={22} type="hash" color="neutral" opacity={getOpacity(0.42)} />
            </div>
          </>
        );

      case 'pricing':
        return (
          <>
            {/* Top left concentric circle */}
            <div className="absolute top-12 left-12 hidden md:block">
              <CircleOutline size={95} color="primary" opacity={getOpacity(0.46)} concentric animate />
            </div>
            {/* Top left dashed arc */}
            <div className="absolute top-6 left-6 hidden lg:block">
              <DottedArc width={150} height={100} color="primary" opacity={getOpacity(0.40)} direction="left" />
            </div>
            {/* Top right stacked diamond */}
            <div className="absolute top-24 right-20 hidden md:block">
              <RoundedDiamond size={56} color="primary" opacity={getOpacity(0.46)} rotation={45} stacked />
            </div>
            {/* Left mid dashed arc */}
            <div className="absolute top-40 left-24 hidden lg:block">
              <DottedArc width={130} height={85} color="secondary" opacity={getOpacity(0.36)} direction="left" variant="dotted" />
            </div>
            {/* Top right accent marks */}
            <div className="absolute top-34 right-12 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.55)} />
            </div>
            {/* Bottom left filled diamond */}
            <div className="absolute bottom-32 left-16 hidden lg:block">
              <RoundedDiamond size={42} color="secondary" filled opacity={getOpacity(0.40)} rotation={38} />
            </div>
            {/* Bottom right accents */}
            <div className="absolute bottom-48 right-32 hidden lg:block">
              <AccentMark size={18} type="plus" color="primary" opacity={getOpacity(0.48)} />
            </div>
            {/* Right dashed arc */}
            <div className="absolute top-52 right-6 hidden lg:block">
              <DottedArc width={110} height={75} color="primary" opacity={getOpacity(0.36)} direction="right" />
            </div>
            {/* Additional circles */}
            <div className="absolute top-60 left-8 hidden lg:block">
              <CircleOutline size={58} color="secondary" opacity={getOpacity(0.38)} />
            </div>
            <div className="absolute bottom-20 right-14 hidden lg:block">
              <CircleOutline size={50} color="primary" opacity={getOpacity(0.36)} concentric />
            </div>
            {/* Scattered accents */}
            <div className="absolute bottom-56 left-28 hidden lg:block">
              <AccentMark size={16} type="dot" color="primary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute top-76 right-28 hidden lg:block">
              <AccentMark size={20} type="hash" color="secondary" opacity={getOpacity(0.42)} />
            </div>
            {/* Bottom right diamond */}
            <div className="absolute bottom-12 right-8 hidden lg:block">
              <RoundedDiamond size={34} color="primary" opacity={getOpacity(0.36)} rotation={50} />
            </div>
          </>
        );

      case 'signup':
        return (
          <>
            {/* Top right concentric circle */}
            <div className="absolute top-14 right-14 hidden md:block">
              <CircleOutline size={100} color="primary" opacity={getOpacity(0.46)} concentric animate />
            </div>
            {/* Top right dashed arc */}
            <div className="absolute top-8 right-6 hidden lg:block">
              <DottedArc width={130} height={90} color="primary" opacity={getOpacity(0.40)} direction="right" />
            </div>
            {/* Bottom right circle */}
            <div className="absolute bottom-28 right-12 hidden md:block">
              <CircleOutline size={68} color="primary" opacity={getOpacity(0.44)} />
            </div>
            {/* Right stacked diamond */}
            <div className="absolute top-40 right-30 hidden lg:block">
              <RoundedDiamond size={50} color="secondary" opacity={getOpacity(0.48)} rotation={40} stacked />
            </div>
            {/* Right accents */}
            <div className="absolute bottom-48 right-20 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.52)} />
            </div>
            <div className="absolute top-56 right-8 hidden lg:block">
              <AccentMark size={18} type="plus" color="primary" opacity={getOpacity(0.48)} />
            </div>
            {/* Bottom left dashed arc */}
            <div className="absolute bottom-16 left-14 hidden lg:block">
              <DottedArc width={120} height={80} color="primary" opacity={getOpacity(0.38)} direction="left" variant="dotted" />
            </div>
            {/* Left accents */}
            <div className="absolute top-28 left-8 hidden lg:block">
              <AccentMark size={16} type="dot" color="primary" opacity={getOpacity(0.46)} />
            </div>
            <div className="absolute bottom-36 left-24 hidden lg:block">
              <RoundedDiamond size={36} color="primary" opacity={getOpacity(0.40)} rotation={50} />
            </div>
            {/* Additional elements */}
            <div className="absolute top-68 right-24 hidden lg:block">
              <AccentMark size={14} type="dot" color="secondary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute bottom-60 right-36 hidden lg:block">
              <AccentMark size={20} type="hash" color="primary" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute top-20 left-20 hidden lg:block">
              <CircleOutline size={45} color="secondary" opacity={getOpacity(0.36)} />
            </div>
            {/* Bottom left circle */}
            <div className="absolute bottom-10 left-6 hidden lg:block">
              <CircleOutline size={55} color="secondary" opacity={getOpacity(0.38)} concentric />
            </div>
          </>
        );

      case 'minimal':
        return (
          <>
            {/* Top right circle */}
            <div className="absolute top-12 right-12 hidden md:block">
              <CircleOutline size={72} color="primary" opacity={getOpacity(0.42)} concentric />
            </div>
            {/* Bottom left diamond */}
            <div className="absolute bottom-16 left-16 hidden lg:block">
              <RoundedDiamond size={46} color="primary" opacity={getOpacity(0.40)} rotation={45} stacked />
            </div>
            {/* Top left accent */}
            <div className="absolute top-24 left-8 hidden lg:block">
              <AccentMark size={18} type="plus" color="secondary" opacity={getOpacity(0.48)} />
            </div>
            {/* Bottom right accent */}
            <div className="absolute bottom-28 right-14 hidden lg:block">
              <AccentMark size={16} type="x" color="primary" opacity={getOpacity(0.45)} />
            </div>
            {/* Dashed arc */}
            <div className="absolute top-8 left-4 hidden lg:block">
              <DottedArc width={100} height={70} color="primary" opacity={getOpacity(0.36)} direction="left" />
            </div>
            {/* Small circle */}
            <div className="absolute bottom-10 right-4 hidden lg:block">
              <CircleOutline size={48} color="secondary" opacity={getOpacity(0.36)} />
            </div>
            {/* Dot accent */}
            <div className="absolute top-40 right-8 hidden lg:block">
              <AccentMark size={12} type="dot" color="secondary" opacity={getOpacity(0.42)} />
            </div>
          </>
        );

      case 'landing-hero':
        return (
          <>
            <div className="absolute top-10 left-10 hidden md:block">
              <CircleOutline size={90} color="primary" opacity={getOpacity(0.44)} concentric animate />
            </div>
            <div className="absolute top-6 left-4 hidden lg:block">
              <DottedArc width={140} height={95} color="primary" opacity={getOpacity(0.38)} direction="left" />
            </div>
            <div className="absolute top-16 right-14 hidden md:block">
              <RoundedDiamond size={52} color="primary" opacity={getOpacity(0.44)} rotation={45} stacked />
            </div>
            <div className="absolute top-48 right-8 hidden lg:block">
              <RoundedDiamond size={40} color="secondary" filled opacity={getOpacity(0.35)} rotation={32} />
            </div>
            <div className="absolute top-32 right-28 hidden lg:block">
              <CircleOutline size={46} color="secondary" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute bottom-24 right-6 hidden lg:block">
              <DottedArc width={120} height={82} color="secondary" opacity={getOpacity(0.36)} direction="right" variant="dotted" />
            </div>
            <div className="absolute bottom-28 right-20 hidden md:block">
              <AccentMark size={20} type="x" color="secondary" opacity={getOpacity(0.50)} />
            </div>
            <div className="absolute bottom-44 right-40 hidden lg:block">
              <AccentMark size={16} type="plus" color="primary" opacity={getOpacity(0.46)} />
            </div>
            <div className="absolute top-56 right-36 hidden lg:block">
              <AccentMark size={14} type="dot" color="primary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute bottom-48 left-12 hidden lg:block">
              <CircleOutline size={60} color="secondary" opacity={getOpacity(0.38)} concentric />
            </div>
            <div className="absolute top-72 left-20 hidden lg:block">
              <AccentMark size={18} type="plus" color="secondary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute top-60 left-6 hidden lg:block">
              <RoundedDiamond size={36} color="primary" opacity={getOpacity(0.34)} rotation={50} />
            </div>
          </>
        );

      case 'landing-content':
        return (
          <>
            <div className="absolute top-16 right-16 hidden md:block">
              <CircleOutline size={82} color="primary" opacity={getOpacity(0.42)} concentric />
            </div>
            <div className="absolute top-8 right-6 hidden lg:block">
              <DottedArc width={130} height={88} color="primary" opacity={getOpacity(0.36)} direction="right" />
            </div>
            <div className="absolute top-44 left-10 hidden lg:block">
              <RoundedDiamond size={50} color="secondary" opacity={getOpacity(0.42)} rotation={42} stacked />
            </div>
            <div className="absolute top-24 left-6 hidden lg:block">
              <RoundedDiamond size={34} color="primary" opacity={getOpacity(0.36)} rotation={55} />
            </div>
            <div className="absolute bottom-36 right-10 hidden md:block">
              <RoundedDiamond size={54} color="primary" opacity={getOpacity(0.40)} rotation={48} stacked animate />
            </div>
            <div className="absolute top-32 left-2 hidden lg:block">
              <DottedArc width={120} height={82} color="secondary" opacity={getOpacity(0.34)} direction="left" variant="dotted" />
            </div>
            <div className="absolute bottom-48 left-16 hidden lg:block">
              <AccentMark size={20} type="x" color="secondary" opacity={getOpacity(0.48)} />
            </div>
            <div className="absolute top-60 left-28 hidden lg:block">
              <AccentMark size={16} type="plus" color="primary" opacity={getOpacity(0.44)} />
            </div>
            <div className="absolute bottom-24 right-28 hidden md:block">
              <CircleOutline size={52} color="secondary" opacity={getOpacity(0.38)} />
            </div>
            <div className="absolute bottom-16 left-32 hidden lg:block">
              <AccentMark size={14} type="dot" color="secondary" opacity={getOpacity(0.42)} />
            </div>
            <div className="absolute bottom-56 right-16 hidden lg:block">
              <AccentMark size={18} type="hash" color="primary" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute bottom-14 left-6 hidden lg:block">
              <CircleOutline size={56} color="primary" opacity={getOpacity(0.34)} concentric />
            </div>
          </>
        );

      case 'seo-hero':
        return (
          <>
            <div className="absolute top-6 left-4 hidden lg:block">
              <DotGrid cols={6} rows={10} gap={16} dotSize={2.5} color="primary" opacity={getOpacity(0.22)} />
            </div>
            <div className="absolute top-8 right-6 hidden lg:block" style={{ transform: 'rotate(15deg)' }}>
              <DotGrid cols={5} rows={8} gap={18} dotSize={2} color="accent" opacity={getOpacity(0.18)} />
            </div>
            <div className="absolute bottom-32 left-16 hidden lg:block">
              <WaveLine width={260} height={50} color="primary" opacity={getOpacity(0.28)} variant="double" />
            </div>
            <div className="absolute top-20 right-20 hidden md:block">
              <GlowOrb size={160} color="accent" opacity={getOpacity(0.22)} animate />
            </div>
            <div className="absolute bottom-16 right-12 hidden lg:block">
              <CrosshatchPattern width={100} height={100} gap={12} color="secondary" opacity={getOpacity(0.14)} strokeWidth={1.2} />
            </div>
            <div className="absolute top-14 left-20 hidden md:block">
              <CircleOutline size={110} color="primary" opacity={getOpacity(0.32)} concentric animate />
            </div>
            <div className="absolute bottom-28 right-40 hidden lg:block">
              <RoundedDiamond size={60} color="secondary" opacity={getOpacity(0.35)} rotation={45} stacked />
            </div>
            <div className="absolute top-48 left-8 hidden lg:block">
              <AccentMark size={24} type="plus" color="secondary" opacity={getOpacity(0.45)} />
            </div>
            <div className="absolute bottom-44 left-36 hidden lg:block">
              <AccentMark size={20} type="x" color="accent" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute top-72 right-10 hidden lg:block">
              <AccentMark size={16} type="hash" color="primary" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'seo-content':
        return (
          <>
            <div className="absolute top-8 right-4 hidden lg:block" style={{ transform: 'rotate(-8deg)' }}>
              <DotGrid cols={7} rows={6} gap={16} dotSize={2.5} color="secondary" opacity={getOpacity(0.20)} />
            </div>
            <div className="absolute bottom-12 left-6 hidden lg:block">
              <WaveLine width={200} height={45} color="accent" opacity={getOpacity(0.30)} variant="single" />
            </div>
            <div className="absolute top-20 left-10 hidden md:block">
              <GlowOrb size={140} color="primary" opacity={getOpacity(0.20)} />
            </div>
            <div className="absolute bottom-36 right-8 hidden lg:block">
              <CrosshatchPattern width={90} height={90} gap={12} color="primary" opacity={getOpacity(0.12)} />
            </div>
            <div className="absolute top-40 left-16 hidden lg:block">
              <RoundedDiamond size={55} color="secondary" opacity={getOpacity(0.38)} rotation={40} stacked animate />
            </div>
            <div className="absolute bottom-20 left-28 hidden lg:block">
              <CircleOutline size={70} color="accent" opacity={getOpacity(0.30)} concentric />
            </div>
            <div className="absolute top-16 right-28 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.45)} />
            </div>
            <div className="absolute bottom-48 right-24 hidden lg:block">
              <AccentMark size={18} type="plus" color="accent" opacity={getOpacity(0.42)} />
            </div>
            <div className="absolute top-60 left-6 hidden lg:block">
              <AccentMark size={14} type="dot" color="primary" opacity={getOpacity(0.40)} />
            </div>
          </>
        );

      case 'seo-features':
        return (
          <>
            <div className="absolute top-12 left-4 hidden lg:block">
              <DotGrid cols={5} rows={12} gap={14} dotSize={2} color="accent" opacity={getOpacity(0.18)} />
            </div>
            <div className="absolute bottom-8 right-4 hidden lg:block" style={{ transform: 'rotate(10deg)' }}>
              <DotGrid cols={5} rows={8} gap={16} dotSize={2.5} color="primary" opacity={getOpacity(0.16)} />
            </div>
            <div className="absolute top-8 right-12 hidden lg:block">
              <WaveLine width={240} height={55} color="secondary" opacity={getOpacity(0.25)} variant="double" />
            </div>
            <div className="absolute top-32 right-6 hidden md:block">
              <GlowOrb size={130} color="secondary" opacity={getOpacity(0.18)} animate />
            </div>
            <div className="absolute bottom-40 left-12 hidden lg:block">
              <CircleOutline size={95} color="primary" opacity={getOpacity(0.30)} concentric animate />
            </div>
            <div className="absolute top-20 left-24 hidden lg:block">
              <RoundedDiamond size={48} color="accent" opacity={getOpacity(0.35)} rotation={50} stacked />
            </div>
            <div className="absolute bottom-24 right-28 hidden lg:block">
              <RoundedDiamond size={42} color="secondary" filled opacity={getOpacity(0.28)} rotation={35} />
            </div>
            <div className="absolute top-56 right-16 hidden lg:block">
              <AccentMark size={20} type="plus" color="primary" opacity={getOpacity(0.42)} />
            </div>
            <div className="absolute bottom-56 left-20 hidden lg:block">
              <AccentMark size={22} type="x" color="secondary" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute top-44 left-8 hidden lg:block">
              <AccentMark size={16} type="hash" color="accent" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'seo-reviews':
        return (
          <>
            <div className="absolute bottom-6 left-4 hidden lg:block" style={{ transform: 'rotate(-5deg)' }}>
              <DotGrid cols={6} rows={5} gap={18} dotSize={2.5} color="accent" opacity={getOpacity(0.20)} />
            </div>
            <div className="absolute top-10 right-8 hidden lg:block">
              <WaveLine width={180} height={40} color="primary" opacity={getOpacity(0.28)} variant="single" />
            </div>
            <div className="absolute bottom-20 right-10 hidden md:block">
              <GlowOrb size={150} color="accent" opacity={getOpacity(0.20)} animate />
            </div>
            <div className="absolute top-24 left-12 hidden lg:block">
              <CrosshatchPattern width={80} height={80} gap={11} color="primary" opacity={getOpacity(0.12)} />
            </div>
            <div className="absolute top-14 left-20 hidden md:block">
              <CircleOutline size={85} color="primary" opacity={getOpacity(0.32)} concentric />
            </div>
            <div className="absolute bottom-32 right-24 hidden lg:block">
              <RoundedDiamond size={52} color="secondary" opacity={getOpacity(0.36)} rotation={45} stacked />
            </div>
            <div className="absolute top-48 right-12 hidden lg:block">
              <AccentMark size={20} type="x" color="accent" opacity={getOpacity(0.42)} />
            </div>
            <div className="absolute bottom-48 left-28 hidden lg:block">
              <AccentMark size={16} type="plus" color="primary" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute top-36 left-6 hidden lg:block">
              <AccentMark size={14} type="dot" color="secondary" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'seo-faq':
        return (
          <>
            <div className="absolute top-8 right-6 hidden lg:block">
              <DotGrid cols={4} rows={6} gap={16} dotSize={2} color="primary" opacity={getOpacity(0.18)} />
            </div>
            <div className="absolute bottom-16 left-8 hidden lg:block">
              <WaveLine width={160} height={35} color="accent" opacity={getOpacity(0.25)} />
            </div>
            <div className="absolute top-12 left-10 hidden md:block">
              <CircleOutline size={78} color="accent" opacity={getOpacity(0.30)} concentric />
            </div>
            <div className="absolute bottom-12 right-10 hidden lg:block">
              <RoundedDiamond size={46} color="primary" opacity={getOpacity(0.34)} rotation={45} stacked />
            </div>
            <div className="absolute top-40 right-4 hidden lg:block">
              <CrosshatchPattern width={70} height={70} gap={10} color="secondary" opacity={getOpacity(0.10)} />
            </div>
            <div className="absolute bottom-28 right-28 hidden lg:block">
              <AccentMark size={18} type="plus" color="accent" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute top-28 left-6 hidden lg:block">
              <AccentMark size={16} type="x" color="secondary" opacity={getOpacity(0.38)} />
            </div>
          </>
        );

      case 'seo-cta':
        return (
          <>
            <div className="absolute top-10 left-6 hidden lg:block">
              <DotGrid cols={5} rows={7} gap={16} dotSize={2.5} color="neutral" opacity={getOpacity(0.20)} />
            </div>
            <div className="absolute bottom-12 right-8 hidden lg:block" style={{ transform: 'rotate(12deg)' }}>
              <DotGrid cols={4} rows={5} gap={18} dotSize={2} color="neutral" opacity={getOpacity(0.16)} />
            </div>
            <div className="absolute top-28 right-12 hidden lg:block">
              <WaveLine width={200} height={45} color="neutral" opacity={getOpacity(0.25)} variant="double" />
            </div>
            <div className="absolute bottom-24 left-20 hidden md:block">
              <GlowOrb size={120} color="accent" opacity={getOpacity(0.18)} animate />
            </div>
            <div className="absolute top-8 left-24 hidden md:block">
              <CircleOutline size={90} color="neutral" opacity={getOpacity(0.35)} concentric animate />
            </div>
            <div className="absolute bottom-16 right-20 hidden lg:block">
              <RoundedDiamond size={56} color="neutral" opacity={getOpacity(0.32)} rotation={45} stacked />
            </div>
            <div className="absolute top-48 right-6 hidden lg:block">
              <AccentMark size={22} type="x" color="neutral" opacity={getOpacity(0.40)} />
            </div>
            <div className="absolute bottom-40 left-8 hidden lg:block">
              <AccentMark size={18} type="plus" color="neutral" opacity={getOpacity(0.38)} />
            </div>
            <div className="absolute top-36 left-12 hidden lg:block">
              <AccentMark size={14} type="hash" color="neutral" opacity={getOpacity(0.35)} />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
      {renderDecorations()}
    </div>
  );
}
